import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent, faqItems } from '@/mocks/siteInfo';

export default function AboutPage() {
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '久久视频',
    url: 'https://jiujiu.video',
    description: '久久视频是您的高清视频在线观看首选平台，汇聚海量影视、综艺、短视频内容。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      email: 'contact@jiujiu.video',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座15层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="久久视频成立于2020年，是国内领先的高清视频在线观看平台。了解久久视频的品牌故事、口碑见证和联系方式。"
        keywords="久久视频,关于久久视频,久久视频联系方式,久久视频品牌介绍"
        canonicalUrl="/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <main className="pt-20 pb-10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <header className="mb-10 text-center">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              关于久久视频
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto">
              让每一次观看都成为享受
            </p>
          </header>

          <nav className="flex items-center justify-center gap-3 mb-10 flex-wrap" aria-label="关于我们导航">
            <a href="#brand" className="px-4 py-2 text-sm bg-background-100 text-foreground-700 rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap">
              品牌介绍
            </a>
            <a href="#reputation" className="px-4 py-2 text-sm bg-background-100 text-foreground-700 rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap">
              口碑见证
            </a>
            <a href="#contact" className="px-4 py-2 text-sm bg-background-100 text-foreground-700 rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap">
              联系我们
            </a>
            <a href="#faq" className="px-4 py-2 text-sm bg-background-100 text-foreground-700 rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap">
              常见问题
            </a>
          </nav>

          <section id="brand" className="mb-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-building-4-line"></i>
                </span>
                {aboutContent.brandIntro.title}
              </h2>
              <div className="space-y-6">
                {aboutContent.brandIntro.sections.map((section, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-5 md:p-6">
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                      {section.heading}
                    </h3>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="reputation" className="mb-12 bg-background-100 py-10 md:py-14 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                  <i className="ri-star-line"></i>
                </span>
                {aboutContent.reputation.title}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {aboutContent.reputation.stats.map((stat, index) => (
                  <div key={index} className="text-center bg-background-50 rounded-lg p-4">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aboutContent.reputation.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-background-50 rounded-lg p-5">
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                        {testimonial.author[0]}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{testimonial.author}</div>
                        <div className="text-xs text-foreground-500">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="mb-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-600">
                  <i className="ri-mail-line"></i>
                </span>
                {aboutContent.contact.title}
              </h2>
              <p className="text-foreground-600 text-sm mb-6">{aboutContent.contact.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-500">{aboutContent.contact.email}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-3">
                    <i className="ri-phone-line text-lg"></i>
                  </span>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h4>
                  <p className="text-sm text-foreground-500">{aboutContent.contact.phone}</p>
                  <p className="text-xs text-foreground-400 mt-1">{aboutContent.contact.workingHours}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <span className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-50 text-secondary-600 mb-3">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-500">{aboutContent.contact.address}</p>
                </div>
              </div>
            </div>
          </section>

          <section id="faq" className="mb-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-question-line"></i>
                </span>
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer hover:bg-background-200/50 transition-colors list-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                      <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}